import React, { useState, useEffect, useCallback } from 'react'
import { useApp } from '../context/AppContext'
import { useWebSocket } from '../hooks/useWebSocket'
import { GameCanvas } from './GameCanvas'
import { API_BASE } from '../config/api'

export function MultiplayerLobby({ mode, onExit }) {
  const { state, dispatch, stagePresets } = useApp()
  const [lobbyId, setLobbyId] = useState(null)
  const [lobbyState, setLobbyState] = useState(null)
  const [joinCode, setJoinCode] = useState('')
  const [error, setError] = useState(null)
  const [isReady, setIsReady] = useState(false)
  const [matchStarting, setMatchStarting] = useState(false)
  
  const currentUser = state.users.find(u => u.id === state.currentUserId)
  const selectedCharacter = state.characters.find(c => c.id === state.selectedCharacterId)
  const lobby = state.lobby // Get lobby from global state (set when creating/joining)

  // WebSocket connection - pass user info as query params (local auth, not cookies)
  const { connected, sendMessage } = useWebSocket(
    lobbyId, 
    useCallback((data) => {
      switch (data.type) {
        case 'init':
          // Initial state from Durable Object
          if (data.state) {
            // Map existing players from DO, marking them appropriately
            const existingPlayers = (data.players || []).map(p => ({
              id: p.id,
              name: p.displayName,
              isHost: p.isHost,
              ready: p.isHost ? data.state.hostReady : data.state.guestReady,
            }))

            // Extract hostId from players list (since it's not in state)
            const hostPlayer = existingPlayers.find(p => p.isHost)
            const hostId = hostPlayer?.id || lobby?.hostId || lobby?.host_user_id

            setLobbyState({
              hostReady: data.state.hostReady ?? false,
              guestReady: data.state.guestReady ?? false,
              hostId: hostId,
              stageId: data.state.stageId ?? lobby?.stageId,
              players: existingPlayers,
            })
            // Store invite code from DO if available
            if (data.state?.inviteCode) {
              dispatch({ type: 'SET_INVITE_CODE', payload: data.state.inviteCode })
            }
          }
          break
        case 'lobby_state':
          setLobbyState(prev => ({ ...prev, ...data.lobby }))
          break
        case 'player-joined':
          setLobbyState(prev => {
            const existingPlayers = prev?.players || []
            const newPlayer = {
              id: data.userId,
              name: data.displayName,
              isHost: data.isHost,
              ready: false,
            }
            // Avoid duplicates
            const filtered = existingPlayers.filter(p => p.id !== data.userId)
            return {
              ...prev,
              players: [...filtered, newPlayer],
            }
          })
          break
        case 'player-left':
          setLobbyState(prev => ({
            ...prev,
            players: (prev?.players || []).filter(p => p.id !== data.userId),
          }))
          break
        case 'ready-changed':
          setLobbyState(prev => {
            const players = prev?.players || []
            const updatedPlayers = players.map(p =>
              (data.isHost && p.isHost) || (!data.isHost && !p.isHost)
                ? { ...p, ready: data.ready }
                : p
            )
            return {
              ...prev,
              hostReady: data.isHost ? data.ready : prev?.hostReady,
              guestReady: !data.isHost ? data.ready : prev?.guestReady,
              players: updatedPlayers,
            }
          })
          break
        case 'stage-changed':
          dispatch({ type: 'SET_STAGE', payload: data.stageId })
          setLobbyState(prev => ({ ...prev, stageId: data.stageId }))
          break
        case 'match-starting':
          setMatchStarting(true)
          // Store match data from the message
          // Note: host is always player1, guest is always player2
          if (data.matchId) {
            // Merge with existing match data if available (host already has full data from API)
            const existingMatch = state.currentMatch?.id === data.matchId ? state.currentMatch : {}
            dispatch({ 
              type: 'SET_MATCH', 
              payload: { 
                ...existingMatch,
                id: data.matchId,
                player1Id: data.hostId,
                player2Id: data.guestId,
                // Only overwrite character IDs if provided in message
                ...(data.hostCharacter && { player1CharacterId: data.hostCharacter }),
                ...(data.guestCharacter && { player2CharacterId: data.guestCharacter }),
                stageId: data.stageId,
              } 
            })
          }
          break
        case 'match-started':
          // Match is now starting - transition to game
          dispatch({ type: 'SET_PLAY_MODE', payload: 'online' })
          break
      }
    }, [dispatch, lobby, state.currentMatch]),
    currentUser?.id,
    currentUser?.name
  )

  // Create lobby
  const createLobby = async () => {
    try {
      setError(null)
      
      if (!selectedCharacter) {
        setError('Please select a character first')
        return
      }
      
      // Use relative URL if on same domain, otherwise use API_BASE
      const apiUrl = API_BASE ? `${API_BASE}/api/lobbies` : '/api/lobbies'
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hostId: currentUser.id,
          hostName: currentUser.name,
          stageId: state.currentStageId,
          region: 'auto',
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to create lobby: ${response.status}`)
      }

      const data = await response.json()
      setLobbyId(data.lobby.id)
      dispatch({ type: 'SET_LOBBY', payload: data.lobby })
      
      // Save invite code
      if (data.lobby.inviteCode) {
        dispatch({ type: 'SET_INVITE_CODE', payload: data.lobby.inviteCode })
      }
    } catch (err) {
      console.error('Create lobby error:', err)
      setError(err.message)
    }
  }

  // Join lobby
  const joinLobby = async (code) => {
    try {
      setError(null)
      
      if (!selectedCharacter) {
        setError('Please select a character first')
        return
      }
      
      // Use relative URL if on same domain, otherwise use API_BASE
      const apiUrl = API_BASE ? `${API_BASE}/api/lobbies/join` : '/api/lobbies/join'
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          inviteCode: code.toUpperCase(),
          playerId: currentUser.id,
          playerName: currentUser.name,
          characterId: selectedCharacter?.id,
        }),
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.error || `Failed to join lobby: ${response.status}`)
      }

      const data = await response.json()
      setLobbyId(data.lobby.id)
      dispatch({ type: 'SET_LOBBY', payload: data.lobby })
      dispatch({ type: 'SET_INVITE_CODE', payload: data.lobby.inviteCode })
      dispatch({ type: 'SET_STAGE', payload: data.lobby.stageId })
    } catch (err) {
      console.error('Join lobby error:', err)
      setError(err.message)
    }
  }

  // Toggle ready status
  const toggleReady = () => {
    // Check if character is selected
    if (!selectedCharacter) {
      setError('Please select a character first')
      return
    }
    
    const newReady = !isReady
    setIsReady(newReady)
    sendMessage({
      type: 'set-ready',
      ready: newReady,
    })
  }

  // Change stage (host only)
  const changeStage = (stageId) => {
    if (lobbyState?.hostId !== currentUser.id) return

    dispatch({ type: 'SET_STAGE', payload: stageId })
    sendMessage({
      type: 'set-stage',
      stageId,
    })
  }

  // Start match (host only)
  const startMatch = async () => {
    if (lobbyState?.hostId !== currentUser.id) return

    // Check if all players are ready (at least 2 players total, and guest is ready)
    const hasGuest = lobbyState.players?.length >= 1 || lobbyState.guestReady !== undefined
    const allReady = lobbyState.hostReady && lobbyState.guestReady
    if (!hasGuest || !allReady) {
      setError('All players must be ready')
      return
    }

    // Create match first
    try {
      const apiUrl = API_BASE ? `${API_BASE}/api/matches/from-lobby` : '/api/matches/from-lobby'
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lobbyId: lobbyId,
          stageId: state.currentStageId,
          userId: currentUser.id,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to create match')
      }

      const data = await response.json()
      
      // Store match info in state
      dispatch({ type: 'SET_MATCH', payload: data.match })
      
      // Send start-match message with match ID
      sendMessage({
        type: 'start-match',
        matchId: data.match.id,
      })
    } catch (err) {
      console.error('Failed to create match:', err)
      setError(err.message)
    }
  }

  // Leave lobby
  const leaveLobby = () => {
    sendMessage({
      type: 'leave',
    })
    // Give the message time to send before closing
    setTimeout(() => {
      setLobbyId(null)
      setLobbyState(null)
      setIsReady(false)
      dispatch({ type: 'SET_LOBBY', payload: null })
    }, 100)
  }

  // Initial lobby creation or join
  useEffect(() => {
    if (mode === 'host' && !lobbyId) {
      createLobby()
    }
  }, [mode, lobbyId])

  // Loading state while creating/joining
  if (!lobbyId && mode === 'host') {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          {error ? (
            <>
              <div className="text-red-400 mb-4">⚠️ {error}</div>
              <button 
                onClick={onExit}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20"
              >
                Go Back
              </button>
            </>
          ) : (
            <>
              <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-400">Creating lobby...</p>
            </>
          )}
        </div>
      </div>
    )
  }

  // Join lobby form
  if (!lobbyId && mode === 'join') {
    return (
      <div className="max-w-md mx-auto py-10">
        <button
          onClick={onExit}
          className="mb-6 text-gray-400 hover:text-white flex items-center gap-2"
        >
          ← Back
        </button>

        <h2 className="text-2xl font-bold mb-6">Join Lobby</h2>

        {error && (
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 mb-4">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Enter Invite Code
            </label>
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="ABC123"
              maxLength={6}
              className="w-full px-4 py-3 bg-black/30 border border-white/10 rounded-lg text-white text-center text-2xl tracking-widest font-mono uppercase focus:outline-none focus:border-cyan-500/50"
            />
          </div>

          <button
            onClick={() => joinLobby(joinCode)}
            disabled={joinCode.length < 6}
            className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Join
          </button>
        </div>
      </div>
    )
  }

  // Lobby screen
  if (lobbyId && lobbyState) {
    const isHost = lobbyState.hostId === currentUser.id
    const otherPlayer = lobbyState.players.find(p => p.id !== currentUser.id)
    // Check if both host and guest are ready (need at least 2 players total)
    const hasGuest = lobbyState.players.length >= 1 || lobbyState.guestReady !== undefined
    const allReady = hasGuest && lobbyState.hostReady && lobbyState.guestReady

    return (
      <div className="max-w-2xl mx-auto py-6">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => { leaveLobby(); onExit(); }}
            className="text-gray-400 hover:text-white flex items-center gap-2"
          >
            ← Leave
          </button>

          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
            <span className="text-sm text-gray-400">{connected ? 'Connected' : 'Connecting...'}</span>
          </div>
        </div>

        <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-6 mb-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold mb-2">Lobby</h2>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-black/30 rounded-lg">
              <span className="text-gray-400 text-sm">Code:</span>
              <span className="text-xl font-mono font-bold text-cyan-400 tracking-widest">
                {state.multiplayerInviteCode}
              </span>
            </div>
          </div>

          {/* Players */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Host (You) */}
            <div className={`p-4 rounded-xl border ${isHost ? 'border-cyan-500/50 bg-cyan-500/10' : 'border-white/10 bg-black/30'}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center font-bold">
                  {currentUser.name[0]}
                </div>
                <div>
                  <div className="font-bold">{currentUser.name}</div>
                  <div className="text-xs text-gray-400">{isHost ? 'Host' : 'Guest'}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${isReady ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                <span className="text-sm text-gray-400">{isReady ? 'Ready' : 'Not Ready'}</span>
              </div>
            </div>

            {/* Other Player */}
            <div className={`p-4 rounded-xl border ${otherPlayer ? 'border-white/10 bg-black/30' : 'border-dashed border-white/20 bg-black/10'}`}>
              {otherPlayer ? (
                <>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center font-bold">
                      {otherPlayer.name[0]}
                    </div>
                    <div>
                      <div className="font-bold">{otherPlayer.name}</div>
                      <div className="text-xs text-gray-400">Guest</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${otherPlayer.ready ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                    <span className="text-sm text-gray-400">{otherPlayer.ready ? 'Ready' : 'Not Ready'}</span>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <span className="text-2xl mb-2">⏳</span>
                  <span className="text-sm">Waiting for player...</span>
                </div>
              )}
            </div>
          </div>

          {/* Character Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Your Fighter {selectedCharacter ? `(${selectedCharacter.name})` : ''}
            </label>
            {state.characters.length > 0 ? (
              <div className="grid grid-cols-4 gap-2">
                {state.characters.map((char) => (
                  <button
                    key={char.id}
                    onClick={async () => {
                      dispatch({ type: 'SELECT_CHARACTER', payload: char.id })
                      
                      // If in a lobby, update the character in the lobby
                      if (lobbyId) {
                        try {
                          const apiUrl = API_BASE 
                            ? `${API_BASE}/api/lobbies/${lobbyId}/character` 
                            : `/api/lobbies/${lobbyId}/character`
                          
                          await fetch(apiUrl, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              characterId: char.id,
                            }),
                          })
                        } catch (err) {
                          console.error('Failed to update lobby character:', err)
                        }
                      }
                    }}
                    className={`p-3 rounded-lg text-left transition-all ${
                      state.selectedCharacterId === char.id
                        ? 'bg-cyan-500/20 border border-cyan-500/50'
                        : 'bg-black/30 border border-white/10 hover:bg-white/5'
                    }`}
                  >
                    <div className="font-medium text-sm truncate">{char.name}</div>
                    <div className="text-xs text-gray-500 truncate">{char.specialName}</div>
                    <div className="flex gap-1 mt-2">
                      <div 
                        className="w-3 h-3 rounded-full border border-white/20" 
                        style={{ backgroundColor: char.bodyColor }}
                      />
                      <div 
                        className="w-3 h-3 rounded-full border border-white/20" 
                        style={{ backgroundColor: char.accentColor }}
                      />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-black/30 rounded-lg text-center text-gray-400">
                <p className="mb-2">No characters created yet</p>
                <button 
                  onClick={() => onExit()}
                  className="text-cyan-400 hover:underline text-sm"
                >
                  Go to Characters to create one →
                </button>
              </div>
            )}
          </div>

          {/* Stage Selection (Host only) */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Stage {isHost ? '(Click to change)' : '(Host selects)'}
            </label>
            <div className="grid grid-cols-5 gap-2">
              {Object.values(stagePresets).map((stage) => (
                <button
                  key={stage.id}
                  onClick={() => changeStage(stage.id)}
                  disabled={!isHost}
                  className={`p-2 rounded-lg text-xs font-bold text-center transition-all ${
                    state.currentStageId === stage.id
                      ? 'bg-cyan-500 text-black'
                      : 'bg-black/30 text-gray-400 hover:bg-black/50'
                  } ${!isHost && 'cursor-not-allowed opacity-50'}`}
                >
                  {stage.name}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm mb-4">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={toggleReady}
              className={`flex-1 py-3 rounded-lg font-bold transition-all ${
                isReady
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                  : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
              }`}
            >
              {isReady ? '✓ Ready' : 'Ready Up'}
            </button>

            {isHost && (
              <button
                onClick={startMatch}
                disabled={!allReady}
                className="flex-1 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-black font-bold rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Start Match
              </button>
            )}
          </div>
        </div>

        {/* Match Starting Overlay */}
        {matchStarting && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
            <div className="text-center">
              <div className="text-4xl font-black mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
                MATCH STARTING
              </div>
              <div className="w-16 h-16 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return null
}
